import { type ReactNode } from 'react';
import { Link } from '../../../components/Link';
import { ORG_ID, SITE_ORIGIN, Seo, breadcrumbSchema, faqSchema } from '../../../lib/seo';
import { PublicFooter } from '../../../components/PublicFooter';
import { PublicHeader } from '../../../components/PublicHeader';
import { Button, cn } from '../../../components/ui';
import { LearnCrossLink } from '../../../components/LearnCrossLink';

/**
 * Shared shell for every /tools/* page. Each page passes in a content object;
 * the layout owns SEO + structured data emission, hero, educational sections,
 * and the CTA strip. Because each page passes its own slug, title, copy, FAQ,
 * and the tool body itself, every URL still ships unique HTML — the layout
 * just keeps the marketing chrome consistent across the tool hub.
 *
 * Three JSON-LD payloads are stacked on every page:
 *   - SoftwareApplication → the tool itself shows up as a rich SERP card.
 *   - FAQPage             → Google can show the Q&A inline (big CTR boost).
 *   - BreadcrumbList      → "Home › Tools › <tool>" trail in the SERP.
 */
export interface ToolPageContent {
  /** URL slug under /tools/<slug>. */
  slug: string;
  /** H1 + browser tab title (the SEO keyword phrase). */
  title: string;
  /** One-line tagline below H1. */
  tagline: string;
  /** Eyebrow chip above H1 — "Free tool · No signup", etc. */
  eyebrow: string;
  /** Page-level description (also goes into <meta description>). ≤ 160 chars. */
  description: string;
  /** Icon for the hero corner. */
  icon: ReactNode;
  /** Accent palette — kept consistent with the rest of the marketing site. */
  accent: 'accent' | 'allow' | 'verify' | 'review';
  /** Optional opening blurb beneath the title — supports markdown-ish soft formatting. */
  intro?: ReactNode;
  /** The interactive tool itself (form + result UI). */
  body: ReactNode;
  /** Why-it-matters / educational H2 section — body of text + optional bullets. */
  educational?: {
    title: string;
    paragraphs: string[];
  };
  /** SoftwareApplication name. Defaults to title. */
  appName?: string;
  /** SEO FAQ block — rendered as <details> + emitted as FAQPage JSON-LD. */
  faq: Array<{ q: string; a: string }>;
  /** Cross-link rail at the bottom — typically the 2-3 adjacent tools. */
  relatedTools: Array<{ slug: string; title: string; tagline: string }>;
  /** Headline of the bottom CTA block. */
  ctaTitle: string;
  /** Sub-copy of the bottom CTA block. */
  ctaBody: string;
}

const ACCENT: Record<
  ToolPageContent['accent'],
  { text: string; border: string; from: string; badge: string }
> = {
  accent: { text: 'text-accent', border: 'border-accentdeep/40', from: 'from-accentdeep/10', badge: 'bg-accentdeep/10 text-accent' },
  allow: { text: 'text-allow', border: 'border-allow/30', from: 'from-allow/10', badge: 'bg-allow/10 text-allow' },
  verify: { text: 'text-verify', border: 'border-verify/30', from: 'from-verify/10', badge: 'bg-verify/10 text-verify' },
  review: { text: 'text-review', border: 'border-review/30', from: 'from-review/10', badge: 'bg-review/10 text-review' },
};

export function ToolPageLayout({ content }: { content: ToolPageContent }) {
  const tone = ACCENT[content.accent];
  const origin = SITE_ORIGIN;
  // Trailing-slash form — feeds both the SoftwareApplication `url` and the breadcrumb leaf, and
  // must equal the page canonical (nginx 301s the bare form).
  const url = `${origin}/tools/${content.slug}/`;

  return (
    <>
      <Seo
        title={content.title}
        description={content.description}
        path={`/tools/${content.slug}`}
        keywords={[content.title, 'free tool', 'email deliverability'].join(', ')}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            // Per-tool @id, NOT the product's SOFTWARE_ID: each free tool is a genuinely
            // distinct application, so they must not collapse onto the SieveGuard product
            // entity (that would attach "price: 0" to the paid product — the exact
            // "SieveGuard is free" misread the homepage `offers` removal fixed).
            //
            // But they must not be ANONYMOUS either. A node with no @id cannot be
            // referenced or merged, so every re-crawl is free to treat it as a new entity
            // and nothing else on the site can point at it. A stable per-tool @id keeps
            // all 15 individually addressable while `publisher` ties them back to the one
            // Organization. See the entity-graph section of docs/SEO.md.
            '@id': `${url}#software`,
            name: content.appName ?? content.title,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            url,
            description: content.description,
            publisher: { '@id': ORG_ID },
          },
          faqSchema(content.faq),
          breadcrumbSchema([
            { name: 'Home', url: `${origin}/` },
            { name: 'Free tools', url: `${origin}/tools` },
            { name: content.title, url },
          ]),
        ]}
      />
    <div className="mx-auto max-w-[1180px] overflow-x-clip px-6 pb-20">
      <PublicHeader />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section
        className={cn(
          'mt-8 overflow-hidden rounded-3xl border bg-gradient-to-br to-panel/60 p-8 md:p-10',
          tone.border,
          tone.from,
        )}
      >
        <div className="grid items-center gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em]',
                tone.badge,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', tone.text.replace('text-', 'bg-'))} />
              {content.eyebrow}
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
              {content.title}
            </h1>
            <p className={cn('mt-3 text-base font-medium', tone.text)}>{content.tagline}</p>
            {content.intro && (
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted">{content.intro}</p>
            )}
          </div>
          <div className="hidden lg:flex lg:justify-end">
            <div
              className={cn(
                'flex h-28 w-28 items-center justify-center rounded-3xl border bg-panel2',
                tone.border,
                tone.text,
              )}
            >
              <div className="scale-[2.2]">{content.icon}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tool body ─────────────────────────────────────────────────── */}
      <section className="mt-10">{content.body}</section>

      {/* ── Educational ───────────────────────────────────────────────── */}
      {content.educational && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            {content.educational.title}
          </h2>
          <div className="mt-5 flex max-w-3xl flex-col gap-4 text-[14.5px] leading-relaxed text-muted">
            {content.educational.paragraphs.map((p) => (
              <p key={p.slice(0, 32)}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* ── Background reading ────────────────────────────────────────── */}
      {/* Placed between the educational block and the FAQ on purpose: a reader who has run the
          tool and read the short explanation is exactly the person who wants the long one. */}
      <LearnCrossLink path={`/tools/${content.slug}`} />

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      {content.faq.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">FAQ</h2>
          <div className="mt-6 flex max-w-3xl flex-col gap-2">
            {content.faq.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-bordersoft bg-panel p-5 transition open:bg-panel2"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-[14.5px] font-semibold">
                  {f.q}
                  <span className={cn('font-mono text-[12px] transition group-open:rotate-45', tone.text)}>+</span>
                </summary>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ── Bottom CTA ────────────────────────────────────────────────── */}
      <section
        className={cn(
          'mt-16 overflow-hidden rounded-3xl border bg-gradient-to-r to-panel/80 p-10 text-center',
          tone.border,
          tone.from,
        )}
      >
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{content.ctaTitle}</h2>
        <p className="mx-auto mt-3 max-w-md text-[14px] text-muted">{content.ctaBody}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/register">
            <Button variant="primary" className="h-12 px-6">Create your workspace</Button>
          </Link>
          <Link to="/features">
            <Button variant="ghost" className="h-12 px-6">See all features →</Button>
          </Link>
        </div>
      </section>

      {/* ── Related tools ─────────────────────────────────────────────── */}
      {content.relatedTools.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-lg font-bold tracking-tight">Other free tools</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {content.relatedTools.map((r) => (
              <Link
                key={r.slug}
                to={`/tools/${r.slug}`}
                className="rounded-2xl border border-bordersoft bg-panel p-5 transition hover:border-accentdeep hover:bg-panel2"
              >
                <div className="text-[14px] font-semibold">{r.title} →</div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{r.tagline}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
    </>
  );
}

/**
 * Reusable copy-to-clipboard button — every generator surfaces the output as a
 * code block with a copy affordance. Returning a quick "Copied!" state is what
 * users expect; we reset after 2s so it doesn't look stuck.
 */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text);
      }}
      className="rounded-md border border-border bg-panel2 px-3 py-1 text-[12px] font-medium text-muted transition hover:border-accentdeep hover:text-txt"
    >
      {label}
    </button>
  );
}

// Default export required by React Router framework-mode route modules.
export default ToolPageLayout;
